import type { ActivityDTO } from 'src/types/dto';
import type { ActivityCompetencyDTO } from 'src/types/dto/competency.dto';

import { AuthorizationLevel } from 'src/types/authorization';

import { getCompetencyName } from 'src/utils/competency';
import {
  durationSecondsToAllocationDays,
  getSplitActivityTotalDurationDays,
  roundDuration,
} from 'src/utils/duration';
import {
  getProjectOwnerBusinessUnits,
  getProjectOwnerFromActivity,
} from 'src/utils/project-owner-business-unit';
import { isManualPlanEntryFromUrl } from './planbox-schedule-url';

function parseAuthorizationLevel(
  rawLevel: string | AuthorizationLevel | null | undefined
): AuthorizationLevel | undefined {
  const normalizedLevel = rawLevel?.trim().toUpperCase();

  if (!normalizedLevel) {
    return undefined;
  }

  return Object.values(AuthorizationLevel).find(
    (level) => String(level).toUpperCase() === normalizedLevel
  );
}

function activityCompetencyRows(activity: ActivityDTO): ActivityCompetencyDTO[] {
  return activity.activityCompetencies?.length
    ? activity.activityCompetencies
    : activity.activityTemplateCompetencies ?? [];
}

function resolveRequiredCompetencyLevel(
  row: ActivityCompetencyDTO,
  activity: ActivityDTO
): AuthorizationLevel | undefined {
  return (
    parseAuthorizationLevel(row?.level) ??
    parseAuthorizationLevel(activity.minimumRole?.level)
  );
}

export interface CompetencyFilterSnapshot {
  competencyIds: number[];
  competencyLabels: Record<string, string>;
  competencyLevels: Record<number, AuthorizationLevel>;
}

/** Unique competency ids, chip labels, and plan-box-derived required levels. */
export function collectCompetencyFilterFromActivities(
  activities: ActivityDTO[]
): CompetencyFilterSnapshot {
  const byId = new Map<
    number,
    { label: string; level?: AuthorizationLevel }
  >();

  for (const activity of activities) {
    for (const row of activityCompetencyRows(activity)) {
      const comp = row?.competency;
      const id = comp?.id;
      if (id == null) continue;

      const current = byId.get(id);
      const nextLabel = current?.label ?? getCompetencyName(comp);
      const nextLevel = resolveRequiredCompetencyLevel(row, activity);

      if (!current) {
        byId.set(id, { label: nextLabel, level: nextLevel });
        continue;
      }

      if (!nextLevel) {
        continue;
      }

      if (!current.level) {
        byId.set(id, { label: nextLabel, level: nextLevel });
        continue;
      }

      if (current.level === nextLevel) {
        byId.set(id, { label: nextLabel, level: nextLevel });
        continue;
      }

      // Different levels for the same competency cannot be represented as one filter
      // level without assuming a seniority ladder; omit the level and filter by id only.
      byId.set(id, { label: nextLabel, level: undefined });
    }
  }

  const competencyIds = Array.from(byId.keys()).sort((a, b) => a - b);
  const competencyLabels = Object.fromEntries(
    competencyIds.map((id) => [String(id), byId.get(id)?.label ?? String(id)])
  );
  const competencyLevels = Object.fromEntries(
    competencyIds.flatMap((id) => {
      const level = byId.get(id)?.level;
      return level ? [[id, level]] : [];
    })
  ) as Record<number, AuthorizationLevel>;

  return { competencyIds, competencyLabels, competencyLevels };
}

function parseInspectorIdsFromUrl(searchParams: Pick<URLSearchParams, 'get'>): number[] {
  const idsParam = searchParams.get('inspector-ids');
  return idsParam?.split(',').map(Number).filter((n) => !Number.isNaN(n) && n > 0) ?? [];
}

export function hasValidScheduleDateParam(searchParams: Pick<URLSearchParams, 'get'>): boolean {
  const dateParam = searchParams.get('date');
  if (!dateParam) return false;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateParam)) return false;
  const parsed = Date.parse(`${dateParam}T00:00:00Z`);
  return !Number.isNaN(parsed);
}

export type ScheduleUrlSnapshot = {
  inspectorIdsParam: string;
  dateParam: string;
  timezoneParam: string;
};

export function createScheduleUrlSnapshot(
  searchParams: Pick<URLSearchParams, 'get'>
): ScheduleUrlSnapshot {
  return {
    inspectorIdsParam: searchParams.get('inspector-ids') ?? '',
    dateParam: searchParams.get('date') ?? '',
    timezoneParam: searchParams.get('timezone') ?? '',
  };
}

function snapshotAsSearchParams(
  snapshot: ScheduleUrlSnapshot
): Pick<URLSearchParams, 'get'> {
  return {
    get: (key: string) => {
      if (key === 'inspector-ids') return snapshot.inspectorIdsParam || null;
      if (key === 'date') return snapshot.dateParam || null;
      if (key === 'timezone') return snapshot.timezoneParam || null;
      return null;
    },
  };
}

/**
 * When the schedule opens with URL-driven view state (inspectors, date, timezone),
 * defer planbox union filter prefill until the user selects a planbox activity.
 * Explicit manual-plan entry is already a planbox workflow, so its filters apply immediately.
 */
export function shouldDeferPlanboxFilterUnionFromUrl(
  searchParams: Pick<URLSearchParams, 'get'>
): boolean {
  if (isManualPlanEntryFromUrl(searchParams)) return false;
  if (parseInspectorIdsFromUrl(searchParams).length > 0) return true;
  if (hasValidScheduleDateParam(searchParams)) return true;
  return Boolean(searchParams.get('timezone')?.trim());
}

/**
 * Detects client-side navigation that deep-links schedule state (e.g. Balance load
 * from the in-schedule activity modal). Ignores in-page inspector filter changes that
 * only update inspector-ids while date/timezone are unchanged.
 */
export function detectScheduleDeepLinkNavigation(
  prev: ScheduleUrlSnapshot,
  next: ScheduleUrlSnapshot
): boolean {
  const prevParams = snapshotAsSearchParams(prev);
  const nextParams = snapshotAsSearchParams(next);

  if (!hasValidScheduleDateParam(prevParams) && hasValidScheduleDateParam(nextParams)) {
    return true;
  }

  const prevTimezone = prev.timezoneParam?.trim() ?? '';
  const nextTimezone = next.timezoneParam?.trim() ?? '';
  if (!prevTimezone && nextTimezone && parseInspectorIdsFromUrl(nextParams).length > 0) {
    return true;
  }

  if (
    hasValidScheduleDateParam(nextParams) &&
    parseInspectorIdsFromUrl(prevParams).length === 0 &&
    parseInspectorIdsFromUrl(nextParams).length > 0
  ) {
    return true;
  }

  return false;
}

/**
 * Activities that drive inspector competency / accreditation-body filter prefill.
 * Uses the same set as the plan box sidebar (CREATED / DRAFTED visits), not every
 * activity still linked on the planbox entity.
 * Selection in the plan box does not narrow this set — use the union of sidebar
 * activities until the user explicitly matches filters via the schedule toolbar.
 */
/** Keep planbox-seeded sidebar visits while GET /activities is still loading. */
export function shouldKeepSeededPlanboxVisitsWhileLoading(params: {
  activitiesLoading: boolean;
  activitiesValidating: boolean;
  visitsDataLength: number;
  visitsLength: number;
}): boolean {
  const { activitiesLoading, activitiesValidating, visitsDataLength, visitsLength } = params;
  return (
    (activitiesLoading || activitiesValidating) &&
    visitsDataLength === 0 &&
    visitsLength > 0
  );
}

/** True when the activity already has real (non-blocker) appointments on the calendar. */
export function activityHasAllocatedAppointments(activity: ActivityDTO | undefined): boolean {
  return (activity?.appointments ?? []).some((appointment) => appointment.type !== 'blocker');
}

export function activityHasRemainingSplitParts(activity: ActivityDTO | undefined): boolean {
  return (activity?.splitParts?.length ?? 0) > 0;
}

/** True when allocated appointment duration is less than the activity total. */
export function activityHasUnallocatedDuration(activity: ActivityDTO | undefined): boolean {
  if (!activity || !activityHasAllocatedAppointments(activity)) {
    return false;
  }

  const totalDurationDays = getSplitActivityTotalDurationDays(activity);
  let hasDurationData = false;
  const allocatedDurationDays = roundDuration(
    (activity.appointments ?? [])
      .filter((appointment) => appointment.type !== 'blocker')
      .reduce((sum, appointment) => {
        if (appointment.durationDays != null) {
          hasDurationData = true;
          return sum + Number(appointment.durationDays);
        }
        if (appointment.durationSeconds != null) {
          hasDurationData = true;
          return sum + durationSecondsToAllocationDays(Number(appointment.durationSeconds));
        }
        return sum;
      }, 0),
  );

  if (!hasDurationData) {
    return false;
  }

  return allocatedDurationDays < roundDuration(totalDurationDays);
}

/** Plan-box multi-split is opt-in. Anything other than `'true'` hides the split button. */
export function isPlanboxMultiSplitEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_PLANBOX_MULTI_SPLIT === 'true';
}

/** Split is only for unsplit plan box activities that are not yet allocated. */
export function canShowPlanboxSplitAction(
  activity: ActivityDTO | undefined,
  hasHandler: boolean,
): boolean {
  if (!isPlanboxMultiSplitEnabled() || !hasHandler || !activity) {
    return false;
  }
  if (activityHasAllocatedAppointments(activity)) {
    return false;
  }
  return !activityHasRemainingSplitParts(activity);
}

/** Combine merges planned split parts back into a single plan box card. */
export function canShowPlanboxCombineAction(
  activity: ActivityDTO | undefined,
  hasHandler: boolean,
): boolean {
  if (!hasHandler || !activity) {
    return false;
  }
  return activityHasRemainingSplitParts(activity);
}

/**
 * Plan-box sidebar should only show activities that can still be drag-allocated.
 * Successful DnD removes membership, but DRAFTED activities with appointments can
 * remain linked (e.g. Plan Manually / failed remove) and must stay hidden unless
 * they still have unallocated planned split parts.
 */
export function filterUnallocatedPlanboxActivities(activities: ActivityDTO[]): ActivityDTO[] {
  return activities.filter(
    (activity) =>
      activityHasRemainingSplitParts(activity) ||
      !activityHasAllocatedAppointments(activity) ||
      activityHasUnallocatedDuration(activity),
  );
}

/**
 * Activity ids that already have a non-blocker appointment on the timeline.
 * Used to hide planbox leftovers before GET /activities hydrates `activity.appointments`
 * (latest-planbox seed often omits that relation, which caused a show-then-vanish flash).
 */
export function allocatedActivityIdsFromCalendarAppointments(
  appointments: Array<{ type?: string; activity?: { id?: number } | null }>
): Set<number> {
  const ids = new Set<number>();
  for (const appointment of appointments) {
    if (appointment.type === 'blocker') continue;
    const activityId = appointment.activity?.id;
    if (activityId != null) {
      ids.add(activityId);
    }
  }
  return ids;
}

export function excludePlanboxActivitiesPresentOnCalendar<T extends { id: number }>(
  activities: T[],
  appointments: Array<{ type?: string; activity?: { id?: number } | null }>
): T[] {
  const allocatedIds = allocatedActivityIdsFromCalendarAppointments(appointments);
  if (allocatedIds.size === 0) {
    return activities;
  }
  return activities.filter((activity) => !allocatedIds.has(activity.id));
}

export function resolvePlanboxFilterActivities(params: {
  sidebarPlanboxActivities: ActivityDTO[];
  deferPlanboxUnionUntilSelection: boolean;
}): ActivityDTO[] {
  const { sidebarPlanboxActivities, deferPlanboxUnionUntilSelection } = params;

  // Balance Load / URL deep-links defer plan-box prefill entirely — including when the
  // sidebar has a single activity. Plan Manually opts out of defer via isManualPlanEntryFromUrl.
  if (deferPlanboxUnionUntilSelection) {
    return [];
  }

  return sidebarPlanboxActivities;
}


export function planboxFilterRequirementFingerprint(activities: ActivityDTO[]): string {
  const competencyFingerprint = activities
    .map((activity) => {
      const parts = activityCompetencyRows(activity)
        .map((row) => {
          const competencyId = row?.competency?.id ?? '';
          const level = resolveRequiredCompetencyLevel(row, activity) ?? '';
          return `${competencyId}:${level}`;
        })
        .sort();
      return `${activity.id}{${parts.join(',')}}`;
    })
    .sort()
    .join('|');

  return competencyFingerprint;
}

/** Fingerprint for plan-box business unit default only (independent of competency deferral). */
export function planboxBusinessUnitFilterFingerprint(activities: ActivityDTO[]): string {
  const ownerBusinessUnitFingerprint = activities
    .map((activity) => {
      const ownerBusinessUnitIds = getProjectOwnerBusinessUnits(
        getProjectOwnerFromActivity(activity)
      ).map((businessUnit) => businessUnit.id);
      return `${activity.id}:bu${ownerBusinessUnitIds.join(',') || 'none'}`;
    })
    .sort()
    .join('|');

  return `ownerBu:${ownerBusinessUnitFingerprint}`;
}

export function shouldSkipPlanboxFilterResync(params: {
  previousActivityIds: number[] | null;
  nextActivityIds: number[];
  previousRequirementFingerprint?: string | null;
  nextRequirementFingerprint?: string;
}): boolean {
  const {
    previousActivityIds,
    nextActivityIds,
    previousRequirementFingerprint = null,
    nextRequirementFingerprint = '',
  } = params;
  if (previousActivityIds == null) {
    return false;
  }

  if (nextActivityIds.length === 0) {
    return true;
  }

  const previousIdSet = new Set(previousActivityIds);
  const nextIdSet = new Set(nextActivityIds);
  const isSameIdSet =
    previousIdSet.size === nextIdSet.size &&
    nextActivityIds.every((id) => previousIdSet.has(id));
  if (isSameIdSet) {
    // Same activities — still resync when competency/level requirements changed.
    return previousRequirementFingerprint === nextRequirementFingerprint;
  }

  const isShrinkOnly =
    nextActivityIds.length < previousActivityIds.length &&
    nextActivityIds.every((id) => previousIdSet.has(id));
  return isShrinkOnly;
}


export type AllocateFailureSideEffect = 'toast_only' | 'remove_from_planbox';

export function resolveAllocateFailureSideEffect(
  errorMessage: string | undefined
): AllocateFailureSideEffect {
  if (errorMessage === 'ACTIVITY_ALREADY_ALLOCATED') {
    return 'remove_from_planbox';
  }
  return 'toast_only';
}

/** Competency ids required by an activity. */
export function competencyIdsFromActivity(activity: ActivityDTO | undefined): number[] {
  if (!activity) return [];
  const ids = new Set<number>();
  for (const row of activityCompetencyRows(activity)) {
    const id = row?.competency?.id;
    if (id != null) ids.add(id);
  }
  return Array.from(ids).sort((a, b) => a - b);
}

/** Union of competency ids from all activities in the plan box. */
export function competencyIdsFromPlanboxActivities(activities: ActivityDTO[]): number[] {
  const ids = new Set<number>();
  for (const activity of activities) {
    for (const competencyId of competencyIdsFromActivity(activity)) {
      ids.add(competencyId);
    }
  }
  return Array.from(ids).sort((a, b) => a - b);
}
